import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import {
  COUNTRY_REGIONS,
  ALL_COUNTRIES,
  CURRENCIES as CURRENCIES_MAP,
  getCurrencyByCountry,
  getCurrencyByCode,
  getCountryByCode,
  getCountryDefaults,
  DATE_FORMATS,
  FINANCIAL_YEARS
} from '../config/countries';

const ConfigContext = createContext(null);

// Re-export for backward compatibility
export { COUNTRY_REGIONS, ALL_COUNTRIES as COUNTRIES, getCurrencyByCountry, getCountryDefaults, FINANCIAL_YEARS, DATE_FORMATS };

// Convert currencies map to array for backward compatibility
export const CURRENCIES = Object.values(CURRENCIES_MAP);

// Amount format options
export const AMOUNT_FORMATS = [
  { value: 'indian', label: '1,00,000.00', example: 'Asian system', badge: 'Best', badgeColor: 'green' },
  { value: 'international', label: '100,000.00', example: 'International' },
  { value: 'european', label: '100.000,00', example: 'European' },
  { value: 'none', label: '100000.00', example: 'No separator' },
];

// Time format options
export const TIME_FORMATS = [
  { value: '12h', label: '12-hour', example: '2:30 PM' },
  { value: '24h', label: '24-hour', example: '14:30' },
];

// Week start day options
export const WEEK_DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Default configuration
const DEFAULT_CONFIG = {
  currency: 'INR',
  incomeColor: '#10b981',
  expenseColor: '#ef4444',
  dateFormat: 'DD-MM-YYYY',
  timeFormat: '12h',
  amountFormat: 'indian',
  weekStartDay: 0,
};

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(() => {
    const stored = localStorage.getItem('appConfig');
    return stored ? { ...DEFAULT_CONFIG, ...JSON.parse(stored) } : DEFAULT_CONFIG;
  });
  const [saving, setSaving] = useState(false);

  // Apply CSS variables for income/expense colors
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--income-color', config.incomeColor);
    root.style.setProperty('--expense-color', config.expenseColor);
    localStorage.setItem('appConfig', JSON.stringify(config));
  }, [config]);

  // Listen for storage events to sync config across tabs or after registration
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'appConfig' && e.newValue) {
        try {
          const newConfig = JSON.parse(e.newValue);
          setConfig(prev => ({ ...prev, ...newConfig }));
        } catch (err) {
          console.error('Failed to parse appConfig from storage:', err);
        }
      }
    };

    // Listen for custom event dispatched after registration/login
    const handleConfigUpdate = () => {
      const stored = localStorage.getItem('appConfig');
      if (stored) {
        try {
          const newConfig = JSON.parse(stored);
          setConfig(prev => ({ ...prev, ...newConfig }));
        } catch (err) {
          console.error('Failed to parse appConfig:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('configUpdated', handleConfigUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('configUpdated', handleConfigUpdate);
    };
  }, []);

  // Sync config from user profile on login
  const syncConfigFromUser = useCallback((userConfig) => {
    if (userConfig) {
      setConfig(prev => ({ ...prev, ...userConfig }));
    }
  }, []);

  // Update a single config value
  const updateConfig = useCallback(async (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);

    // Save to backend if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      setSaving(true);
      try {
        await authAPI.updateProfile({ config: newConfig });
        // Update user in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          user.config = newConfig;
          localStorage.setItem('user', JSON.stringify(user));
        }
        // Dispatch saved event for UI feedback
        window.dispatchEvent(new Event('config-saved'));
      } catch (err) {
        console.error('Failed to save config to server:', err);
      } finally {
        setSaving(false);
      }
    }
  }, [config]);

  // Get current currency data
  const getCurrency = useCallback(() => {
    return CURRENCIES.find(c => c.code === config.currency) || CURRENCIES[0];
  }, [config.currency]);

  // Format amount based on current config
  const formatAmount = useCallback((amount, showSymbol = true, forceDecimals = false) => {
    if (amount === null || amount === undefined) return showSymbol ? `${getCurrency().symbol}0.00` : '0.00';

    const currency = getCurrency();
    const hasDecimals = amount % 1 !== 0;
    const absAmount = Math.abs(amount);
    const sign = amount < 0 ? '-' : '';
    // Show decimals if the amount has them or if forceDecimals is true
    const showDecimals = hasDecimals || forceDecimals;

    let formatted;
    switch (config.amountFormat) {
      case 'indian':
        formatted = new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: showDecimals ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(absAmount);
        break;
      case 'international':
        formatted = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: showDecimals ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(absAmount);
        break;
      case 'european':
        formatted = new Intl.NumberFormat('de-DE', {
          minimumFractionDigits: showDecimals ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(absAmount);
        break;
      case 'none':
        formatted = absAmount.toFixed(showDecimals ? 2 : 0);
        break;
      default:
        formatted = absAmount.toString();
    }

    return showSymbol ? `${sign}${currency.symbol}${formatted}` : `${sign}${formatted}`;
  }, [config.amountFormat, getCurrency]);

  // Format time based on current config (time is in HH:mm 24-hour format)
  const formatTime = useCallback((time) => {
    if (!time) return '';

    // Parse HH:mm format
    const [hours, minutes] = time.split(':').map(Number);

    if (config.timeFormat === '24h') {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    // 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  }, [config.timeFormat]);

  // Format date based on current config
  const formatDate = useCallback((date) => {
    if (!date) return '';

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[d.getMonth()];

    switch (config.dateFormat) {
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'MM-DD-YYYY':
        return `${month}-${day}-${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'DD Mon YYYY':
        return `${day} ${monthShort} ${year}`;
      case 'Mon DD, YYYY':
        return `${monthShort} ${day}, ${year}`;
      default:
        return `${day}-${month}-${year}`;
    }
  }, [config.dateFormat]);

  // Format date with time
  const formatDateTime = useCallback((date, time) => {
    const formattedDate = formatDate(date);
    if (!time) return formattedDate;
    const formattedTime = formatTime(time);
    return `${formattedDate}, ${formattedTime}`;
  }, [formatDate, formatTime]);

  const value = {
    config,
    updateConfig,
    syncConfigFromUser,
    saving,
    getCurrency,
    formatAmount,
    formatDate,
    formatTime,
    formatDateTime,
    currencies: CURRENCIES,
    countries: ALL_COUNTRIES,
    countryRegions: COUNTRY_REGIONS,
    getCurrencyByCountry,
    getCountryDefaults,
    dateFormats: DATE_FORMATS,
    timeFormats: TIME_FORMATS,
    amountFormats: AMOUNT_FORMATS,
    weekDays: WEEK_DAYS,
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}
